import React, { useContext, useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { AgEconomicGroup, AgFinancingDocumentType, AgStateName } from '@indigo-ag/schema';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';

import i18next from 'core/i18n';
import { utils } from 'shared';
import { PhaseWrapper } from '../../shared/PhaseWrapper';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { WarrantyType } from 'features/brazil-grower/components/WarrantyType/WarrantyType';
import { GuaranteeDataSection } from './GuaranteeDataSection/GuaranteeDataSection';
import { PledgeCertificateDataSection } from './PledgeCertificateDataSection/PledgeCertificateDataSection';
import { MinutaSection } from './MinutaSection/MinutaSection';
import { ContractDataSection, ContractDataSectionProps } from './ContractDataSection/ContractDataSection';
import { FundAssignmentState } from './FundAssignmentState/FundAssignmentState';
import { addAgFinanceToCurrentCessionGroup, saveEconomicGroups } from 'features/brazil-grower/queries';
import { EconomicGroup } from './EconomicGroup/EconomicGroup';
import { UserContext } from 'core/components/GateWay/GateWay';
import { AddToCessionModal } from '../../shared/AddToCessionModal/AddToCessionModal';

export const Phase4: React.FunctionComponent = () => {
  /**
   * Context's
   */
  const { agFinance, growerInfo } = useContext(FinanceProcessContext);
  const { isCreditAnalyst, isSuper } = useContext(UserContext);
  /**
   * State
   */
  const [isSendingToFund, setIsSendingToFund] = useState(false);
  const [isAddToCessionModalOpen, setIsAddToCessionModalOpen] = useState(false);

  /**
   * Mutations
   */
  const [addAgFinanceToCurrentCessionGroupFn] = useMutation(addAgFinanceToCurrentCessionGroup);
  const [saveEconomicGroupsFn] = useMutation(saveEconomicGroups);

  const { addFlashMessage } = useFlashMessage();

  const minutaFile = agFinance?.phase3?.files?.find(f => f.documentType === AgFinancingDocumentType.REGISTERED_MINUTA);

  const pledgeCertificateFileInfo = agFinance?.phase3?.files?.find(
    f => f.documentType === AgFinancingDocumentType.CERTIFICATE_OF_PLEDGE
  )?.metadata?.phase3FileInfo;

  const getContractInfo = (): ContractDataSectionProps => {
    return {
      contractFile: agFinance?.phase3?.files?.find(f => f.documentType === AgFinancingDocumentType.REGISTERED_CONTRACT),
      opportunity: agFinance?.orderInfo?.opportunities[0],
      campaign: agFinance?.orderInfo?.campaign,
      contractAddress: growerInfo?.additionalInfo?.mailingAddress
    };
  };

  const updateEconomicGroups = (economicGroups: AgEconomicGroup[]) => {
    return saveEconomicGroupsFn({
      variables: {
        data: {
          phase4Id: agFinance?.phase4?.id,
          economicGroups: utils.graphql.removeTypename(economicGroups)
        }
      }
    }).catch((e: ApolloError) => {
      addFlashMessage({
        content: e.graphQLErrors[0]?.message || i18next.t('error'),
        title: '',
        variant: 'error'
      });
    });
  };

  const isEconomicGroupsEditable =
    (isSuper || isCreditAnalyst) &&
    (agFinance?.currentStates?.includes(AgStateName.P4_AWAITING_ASSIGNMENT) ||
      agFinance?.currentStates?.includes(AgStateName.P4_GROUP_RECEIVED_BY_FUND) ||
      agFinance?.currentStates?.includes(AgStateName.P4_GROUP_MANAGER_DENIED) ||
      agFinance?.currentStates?.includes(AgStateName.P4_GROUP_AWAITING_ASSIGNMENT));

  const addToCurrentCession = (cessionNumber: String) => {
    setIsSendingToFund(true);

    return addAgFinanceToCurrentCessionGroupFn({
      variables: {
        agFinancingId: agFinance?.id,
        cessionNumber: Number(cessionNumber)
      }
    })
      .then(() => {
        addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t('addedToCessionSuccessfully')),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => setIsSendingToFund(false));
  };

  const openCessionNumberModal = () => {
    setIsAddToCessionModalOpen(true);
  };

  const closeCessionNumberModal = () => {
    setIsAddToCessionModalOpen(false);
  };

  return (
    <PhaseWrapper
      title={utils.text.toTitleCase(i18next.t('fundAssignment'))}
      subtitleElement={<WarrantyType collateralType={agFinance?.collateralType} />}
      actionElement={
        agFinance && utils.FinancingProcessUtil.readyToAddToCurrentCession(agFinance) ? (
          <>
            <Button
              busy={isSendingToFund}
              className="text-transform-capitalize"
              faIconAfter={faPaperPlane}
              onClick={openCessionNumberModal}
              size="small"
            >
              {i18next.t('addToCession')}
            </Button>
            <AddToCessionModal
              closeModal={closeCessionNumberModal}
              message={utils.text.firstLetterUppercase(i18next.t('addToCessionDisclaimer'))}
              onConfirmAction={addToCurrentCession}
              open={isAddToCessionModalOpen}
              title={utils.text.toTitleCase(i18next.t('addToCession'))}
            />
          </>
        ) : (
          undefined
        )
      }
    >
      <FundAssignmentState agFinance={agFinance} />
      <MinutaSection registrationNumber={agFinance?.phase3?.draftRegistrationNumber} minutaFile={minutaFile} />
      <GuaranteeDataSection />
      <PledgeCertificateDataSection pledgeCertificateFileInfo={pledgeCertificateFileInfo} />
      <ContractDataSection {...getContractInfo()} />
      <EconomicGroup
        economicGroups={agFinance?.phase4?.economicGroups || []}
        onUpdateEconomicGroup={updateEconomicGroups}
        readonly={!isEconomicGroupsEditable}
      />
    </PhaseWrapper>
  );
};
